import { useState, useEffect } from 'react';
import { useList, useCustom, useApiUrl } from '@refinedev/core';
import { formatDate } from '@/lib/utils';
import { HelpTooltip } from '@/components/ui/Tooltip';
import { toast } from '@/hooks/useToast';
import { adminFetch } from '@/providers/admin-api';
import { SortableHeader, type SortOrder } from '@/components/table/SortableHeader';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';

interface WaitlistEntry {
  id: string;
  email: string;
  source_page: string;
  created_at: string;
  referrer: string | null;
  // Contact fields from richer forms (e.g. the /prelaunch flyer). Null for
  // older sources (coming_soon, waiting_list_form).
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  location: string | null;
}

interface WaitlistStats {
  total: number;
  today: number;
  this_week: number;
  by_source: { source_page: string; count: number }[];
}

const PAGE_SIZE_OPTIONS = [20, 50, 100];

export function WaitlistPage() {
  const apiUrl = useApiUrl();
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<WaitlistEntry | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch waitlist stats
  const { data: statsData, refetch: refetchStats } = useCustom<WaitlistStats>({
    url: '/v1/admin/waitlist/stats',
    method: 'get',
  });

  const stats = statsData?.data;

  // Fetch waitlist entries
  const { data, isLoading, isError, refetch } = useList<WaitlistEntry>({
    resource: 'waitlist',
    pagination: {
      current: currentPage,
      pageSize,
    },
    filters: [
      ...(search ? [{ field: 'search', operator: 'contains' as const, value: search }] : []),
      ...(sourceFilter !== 'all' ? [{ field: 'source_page', operator: 'eq' as const, value: sourceFilter }] : []),
    ],
    sorters: [{ field: 'created_at', order: sortOrder }],
  });

  const entries = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  // Clear selections when page/filters change
  useEffect(() => {
    setSelectedEntries(new Set());
  }, [currentPage, pageSize, search, sourceFilter, sortOrder]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleExport = async () => {
    try {
      const response = await adminFetch(`${apiUrl}/v1/admin/waitlist/export`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `waitlist-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Export Complete',
        description: 'Waitlist exported to CSV',
      });
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: 'Could not export waitlist',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (entry: WaitlistEntry) => {
    setDeleteLoading(true);
    try {
      await adminFetch(`${apiUrl}/v1/admin/waitlist/${entry.id}`, {
        method: 'DELETE',
      });

      setShowDeleteModal(false);
      setEntryToDelete(null);
      await refetch();
      await refetchStats();

      toast({
        title: 'Entry Deleted',
        description: `Removed ${entry.email} from waitlist`,
      });
    } catch (error) {
      toast({
        title: 'Delete Failed',
        description: 'Could not delete entry',
        variant: 'destructive',
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedEntries.size === entries.length) {
      setSelectedEntries(new Set());
    } else {
      setSelectedEntries(new Set(entries.map((e) => e.id)));
    }
  };

  const toggleSelectEntry = (entryId: string) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      newSelected.add(entryId);
    }
    setSelectedEntries(newSelected);
  };

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
        Failed to load waitlist. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Waitlist</h2>
          <p className="text-muted-foreground">
            Signups from the marketing website &amp; the /prelaunch flyer — with contact details
          </p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Signups</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Today</p>
                <p className="text-2xl font-bold">{stats.today}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">This Week</p>
                <p className="text-2xl font-bold">{stats.this_week}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
                <Mail className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Sources</p>
                <p className="text-2xl font-bold">{stats.by_source.length}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by email, name, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 w-64 rounded-lg border bg-background pl-10 pr-8 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setCurrentPage(1);
                }}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <button
            type="submit"
            className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Search
          </button>
        </form>

        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Source:</label>
          <select
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value);
              setCurrentPage(1);
            }}
            aria-label="Filter by source page"
            className="h-10 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="all">All sources</option>
            {(stats?.by_source ?? []).map((s) => (
              <option key={s.source_page} value={s.source_page}>
                {s.source_page} ({s.count})
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Show:</label>
          <select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            aria-label="Items per page"
            className="h-10 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Active Filters */}
      {search && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm">
            Search: "{search}"
            <button
              onClick={() => {
                setSearch('');
                setCurrentPage(1);
              }}
              aria-label="Clear search filter"
              className="ml-1 rounded-full hover:bg-primary/20 p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={entries.length > 0 && selectedEntries.size === entries.length}
                    onChange={toggleSelectAll}
                    aria-label="Select all entries"
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Contact</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Location</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  <span className="flex items-center gap-1">
                    Source
                    <HelpTooltip content="The page where the user signed up" side="bottom" />
                  </span>
                </th>
                <SortableHeader
                  field="created_at"
                  label="Signed Up"
                  activeField="created_at"
                  activeOrder={sortOrder}
                  onSort={() => {
                    setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
                    setCurrentPage(1);
                  }}
                />
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Referrer</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: pageSize }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="border-b last:border-0 animate-pulse">
                    <td className="px-4 py-3"><div className="h-4 w-4 rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-48 rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-20 rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-24 rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-32 rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-40 rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-8 rounded bg-muted ml-auto" /></td>
                  </tr>
                ))
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    {search ? 'No matching entries found' : 'No waitlist signups yet'}
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr
                    key={entry.id}
                    className={`border-b last:border-0 hover:bg-muted/30 ${selectedEntries.has(entry.id) ? 'bg-primary/5' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedEntries.has(entry.id)}
                        onChange={() => toggleSelectEntry(entry.id)}
                        aria-label="Select entry"
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        {(entry.first_name || entry.last_name) && (
                          <span className="font-medium">
                            {[entry.first_name, entry.last_name].filter(Boolean).join(' ')}
                          </span>
                        )}
                        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                          <a href={`mailto:${entry.email}`} className="hover:text-foreground hover:underline">{entry.email}</a>
                        </span>
                        {entry.phone && (
                          <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                            <a href={`tel:${entry.phone}`} className="hover:text-foreground hover:underline">{entry.phone}</a>
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {entry.location ? (
                        <span className="flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                          {entry.location}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        entry.source_page === 'prelaunch' ? 'bg-primary/15 text-primary' : 'bg-muted'
                      }`}>
                        {entry.source_page.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatDate(entry.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs truncate" title={entry.referrer || undefined}>
                      {entry.referrer || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end">
                        <button
                          onClick={() => {
                            setEntryToDelete(entry);
                            setShowDeleteModal(true);
                          }}
                          className="rounded-lg p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          title="Delete entry"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {total === 0 ? 0 : (currentPage - 1) * pageSize + 1} to{' '}
              {Math.min(currentPage * pageSize, total)} of {total} entries
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                title="First page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                title="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-2 text-sm">
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                title="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                title="Last page"
              >
                <ChevronsRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && entryToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                <Trash2 className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Delete Entry</h3>
                <p className="text-sm text-muted-foreground">Remove from waitlist</p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Are you sure you want to remove <strong className="text-foreground">{entryToDelete.email}</strong> from the waitlist?
              This action cannot be undone.
            </p>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setEntryToDelete(null);
                }}
                disabled={deleteLoading}
                className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(entryToDelete)}
                disabled={deleteLoading}
                className="flex items-center gap-2 rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
              >
                {deleteLoading && (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
